import { type Character } from '~/models/character';
import { EffectType } from '~/models/effect';
import { usePropertyStore } from '~/stores/property';
import { tierForRank } from '~/composables/characterDerivation';

/**
 * Tier boundaries: T1 = 0-3, T2 = 4-6, T3 = 7-10. Delegates to the
 * canonical `tierForRank` in characterDerivation.ts.
 */
export function tierOf(rank: number): number {
	return tierForRank(rank);
}

/**
 * RankedBoon dice chain — auto-scales per rank.
 *
 * R0 and R1 both start at 1d4 so rank-0 characters see their boon dice
 * during character creation. From R2 onward the chain advances one step
 * per rank. Beyond R6 a second die is appended to the base d20.
 *
 *   R0 → 1d4   R4 → 1d10   R7 → 1d20+1d4   R10 → 1d20+1d10
 *   R1 → 1d4   R5 → 1d12   R8 → 1d20+1d6
 *   R2 → 1d6   R6 → 1d20   R9 → 1d20+1d8
 *   R3 → 1d8
 *
 * Kept in sync with `composables/characterDerivation.ts:getRankedBoonDice`
 * — both consumers (Nuxt hero-doc + Foundry sheet via the shared build)
 * must produce the same values for every rank.
 */
const DICE_CHAIN = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20'];

export function getRankedBoonDice(rank: number): string {
	if (rank < 0) return '';
	// R0 and R1 share the first step of the chain.
	const idx = Math.max(0, rank - 1);
	if (idx < DICE_CHAIN.length) return `1${DICE_CHAIN[idx]}`;
	const overflow = idx - DICE_CHAIN.length;
	if (overflow < DICE_CHAIN.length - 1) return `1d20+1${DICE_CHAIN[overflow]}`;
	return '1d20+1d12'; // cap
}

/**
 * TieredBoon dice — auto-scales per tier.
 * T1(R0-3)=d4, T2(R4-6)=d6, T3(R7-10)=d8
 */
export function getTieredBoonDice(rank: number): string {
	if (rank < 0) return '';
	const tier = tierOf(rank);
	if (tier === 1) return '1d4';
	if (tier === 2) return '1d6';
	return '1d8';
}

/** Count how many tier boundaries are crossed from rankTaken to currentRank (inclusive of starting tier). */
function getTiersSpanned(from: number, to: number): number {
	let count = 1;
	for (let r = from + 1; r <= to; r++) {
		if (tierOf(r) !== tierOf(r - 1)) count++;
	}
	return count;
}

/**
 * Calculate the frequency multiplier for a bonus effect based on the dice field.
 *
 * dice values:
 *   "once" = apply once
 *   "1"    = every rank
 *   "2"    = every 2 ranks
 *   "3"    = every 3 ranks
 *   "tier" = once per tier boundary crossed
 *   "rank" = scales with rank (bonus = adjustment × currentRank)
 */
export function getFrequencyMultiplier(dice: string, rankTaken: number, currentRank: number): number {
	if (!dice || dice === 'once') return 1;
	if (dice === '1') return currentRank - rankTaken + 1;
	if (dice === 'rank') return currentRank;
	if (dice === 'tier') return getTiersSpanned(rankTaken, currentRank);

	const interval = parseInt(dice);
	if (!isNaN(interval) && interval > 1) {
		return Math.floor((currentRank - rankTaken) / interval) + 1;
	}

	return 1;
}

/**
 * Check if a bonus effect applies at a specific rank based on its frequency.
 */
function qualifiesAtRank(dice: string, rankTaken: number, currentRank: number): boolean {
	if (!dice || dice === 'once') return rankTaken === currentRank;
	if (dice === '1' || dice === 'rank') return currentRank >= rankTaken;
	if (dice === 'tier') {
		if (currentRank === rankTaken) return true;
		return currentRank > rankTaken && tierOf(currentRank) !== tierOf(currentRank - 1);
	}
	const interval = parseInt(dice);
	if (!isNaN(interval) && interval > 1) {
		return (currentRank - rankTaken) % interval === 0;
	}
	return rankTaken === currentRank;
}

export const useEffectScanner = () => {
	const propertyStore = usePropertyStore();

	/**
	 * Scan for cumulative point-in-time totals (HP, wounds).
	 * Uses frequency multiplier to calculate total bonus at the given rank.
	 * e.g., bonusHP +5 per rank, taken at rank 0, checked at rank 8 = 5 × 9 = 45
	 */
	async function scanEffectBonuses(
		rank: number,
		character: Character,
		effectType: EffectType,
		filter?: { value?: string }
	): Promise<number> {
		let total = 0;

		for (let r = 0; r <= rank && r < character.details.length; r++) {
			for (const feature of character.details[r]?.features ?? []) {
				const property = propertyStore.propertyById(feature.id);
				if (!property) continue;

				await propertyStore.loadEffects(property);

				for (const effect of property.effects) {
					if (effect.type !== effectType) continue;
					if (filter?.value !== undefined && effect.value !== filter.value) continue;

					const multiplier = getFrequencyMultiplier(effect.dice, r, rank);
					total += effect.adjustment * multiplier;
				}
			}
		}

		return total;
	}

	/**
	 * Scan for flat per-rank pool bonuses (SP, AP, stat points).
	 * Returns the flat bonus for a SINGLE rank. The carry-forward system
	 * handles accumulation across ranks.
	 * e.g., bonusSP +1 per rank = flat +1 at each qualifying rank.
	 */
	async function scanFlatBonusForRank(
		rank: number,
		character: Character,
		effectType: EffectType,
		filter?: { value?: string }
	): Promise<number> {
		let total = 0;

		for (let r = 0; r <= rank && r < character.details.length; r++) {
			for (const feature of character.details[r]?.features ?? []) {
				const property = propertyStore.propertyById(feature.id);
				if (!property) continue;

				await propertyStore.loadEffects(property);

				for (const effect of property.effects) {
					if (effect.type !== effectType) continue;
					if (filter?.value !== undefined && effect.value !== filter.value) continue;

					if (qualifiesAtRank(effect.dice, r, rank)) {
						total += effect.dice === 'rank'
							? effect.adjustment * rank
							: effect.adjustment;
					}
				}
			}
		}

		return total;
	}

	return { scanEffectBonuses, scanFlatBonusForRank, getFrequencyMultiplier };
};
